import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Look, lookProps, lookSurface } from '@/entities/appearance';
import { type Database, usePaletteOptions, useUpdateDatabase } from '@/entities/database';
import { isLookValid, LookEditor, lookPayload } from '@/features/appearance-editor';
import { FormError } from '@/shared/forms';
import { Button, Dialog, DialogActions, Icons, PalettePicker } from '@/shared/ui';

import styles from './DatabaseCatalog.module.css';

const TILE_STYLES = ['header', 'border', 'accent', 'soft', 'solid', 'gradient', 'pattern'] as const;

/**
 * The appearance of a database CARD: the identification color (it also marks the database page)
 * and the card's look in catalogs. The opened database - its tables, records and cell formatting -
 * is never touched. Offered to those who may edit the database's details.
 */
export function DatabaseAppearanceDialog({
  database,
  onClose,
}: {
  database: Database;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateDatabase(database.id);
  const options = usePaletteOptions();
  const [color, setColor] = useState<string | null>(database.color);
  const [tile, setTile] = useState<Look | null>(database.appearance?.tile ?? null);
  const payload = lookPayload(tile);
  const props = lookProps(payload);
  return (
    <Dialog
      title={t('catalog.databaseAppearanceTitle', { name: database.name })}
      onClose={onClose}
      size="lg"
    >
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          update.mutate(
            { color, appearance: payload ? { v: 1, tile: payload } : null },
            { onSuccess: onClose },
          );
        }}
      >
        <div
          className={`${styles.appearancePreview} ${lookSurface ?? ''}`}
          data-testid="database-card-preview"
          role="img"
          aria-label={t('catalog.databasePreview')}
          data-color={color ?? undefined}
          {...props}
          style={{ ...props.style, ['--database-color' as string]: color ?? 'transparent' }}
        >
          <span className={styles.cardColor} aria-hidden />
          <span className={styles.cardName}>
            <Icons.layers />
            <strong>{database.name}</strong>
          </span>
        </div>
        <p className={styles.hint}>{t('catalog.databaseAppearanceHint')}</p>
        <PalettePicker
          label={t('catalog.databaseColor')}
          value={color}
          options={options}
          noneLabel={t('databases.palette.none')}
          custom
          disabled={update.isPending}
          onPick={setColor}
        />
        <h3 className={styles.sectionHeading}>{t('catalog.databaseCardLook')}</h3>
        <LookEditor value={tile} onChange={setTile} allowed={TILE_STYLES} idPrefix="database" />
        <FormError error={update.error} />
        <DialogActions>
          <Button
            variant="ghost"
            onClick={() => {
              setColor(null);
              setTile(null);
            }}
          >
            {t('boards.resetAppearance')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending || !isLookValid(tile)}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
